import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { List, X } from "@phosphor-icons/react";

const NAV_LINKS = [
  { label: "The Book", id: "book" },
  { label: "About", id: "author" },
  { label: "Get the Book", id: "purchase" },
];

export default function Header({ onNavigate }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNav = (id) => {
    onNavigate(id);
    setMobileOpen(false);
  };

  return (
    <motion.header
      data-testid="header-nav"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "glass border-b"
          : "bg-transparent"
      }`}
      style={{
        borderColor: scrolled ? "rgba(234,208,161,0.08)" : "transparent",
      }}
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <button
          data-testid="logo-link"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="font-serif text-lg md:text-xl tracking-wide"
          style={{
            fontFamily: "'Playfair Display', serif",
            color: "var(--gold)",
            background: "none",
            border: "none",
            cursor: "pointer",
          }}
        >
          Ulrika Logart
        </button>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-10" data-testid="desktop-nav">
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              data-testid={`nav-${link.id}`}
              onClick={() => handleNav(link.id)}
              className="text-sm tracking-widest uppercase transition-colors duration-300 bg-transparent border-none cursor-pointer"
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontWeight: 400,
                color: "var(--text-secondary)",
                letterSpacing: "0.12em",
              }}
              onMouseEnter={(e) => (e.target.style.color = "var(--gold)")}
              onMouseLeave={(e) => (e.target.style.color = "var(--text-secondary)")}
            >
              {link.label}
            </button>
          ))}
        </nav>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden bg-transparent border-none cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
          style={{ color: "var(--gold)" }}
        >
          {mobileOpen ? <X size={24} /> : <List size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden glass border-t px-6 py-6 flex flex-col gap-5"
          style={{ borderColor: "rgba(234,208,161,0.08)" }}
          data-testid="mobile-nav"
        >
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              data-testid={`mobile-nav-${link.id}`}
              onClick={() => handleNav(link.id)}
              className="text-sm tracking-widest uppercase text-left bg-transparent border-none cursor-pointer"
              style={{
                fontFamily: "'Outfit', sans-serif",
                color: "var(--text-secondary)",
                letterSpacing: "0.12em",
              }}
            >
              {link.label}
            </button>
          ))}
        </motion.div>
      )}
    </motion.header>
  );
}
